import { Group, Text } from '@repletoryreach/sdk-ui';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { Pagination } from '@repletoryreach/sdk-ui/data-display';
import { formatInteger } from '../lib/format';
import { useT } from '../i18n/I18nProvider';
import './table-footer.css';

const SIZES = [10, 25, 50, 100];

/**
 * What the operator is looking at, and how to see the rest: the range on the
 * left, the page size and the pager on the right.
 *
 * `capped` marks a list the server truncated. Saying "1 to 25 of 500" when 500
 * is a server ceiling rather than the real total is the quiet lie this footer
 * exists to avoid, so pass it whenever the response came back at the limit.
 */
export function TableFooter({ page, pageSize, total, capped = false, onPageChange, onPageSizeChange }: {
  page: number;
  pageSize: number;
  total: number;
  capped?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) {
  const t = useT();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <Group justify="space-between" align="center" gap="md" wrap="wrap" className="rr-table-footer">
      <Text role="caption" c="var(--rr-color-text-muted)">
        {capped
          ? t('table.footer.rangeCapped', { from: formatInteger(from), to: formatInteger(to), total: formatInteger(total) })
          : t('table.footer.range', { from: formatInteger(from), to: formatInteger(to), total: formatInteger(total) })}
      </Text>
      <Group gap="sm" align="center" wrap="nowrap">
        <Select
          aria-label={t('table.footer.perPage')}
          value={String(pageSize)}
          onChange={(e) => onPageSizeChange(Number(e.currentTarget.value))}
          data={SIZES.map((n) => ({ value: String(n), label: String(n) }))}
        />
        {pages > 1 && <Pagination value={page} total={pages} onChange={onPageChange} withEdges siblings={1} size="sm" />}
      </Group>
    </Group>
  );
}
